import React, { useState } from "react";
import { 
  Container, 
  Typography, 
  Stack, 
  TextField, 
  Button, 
  Box, 
  Paper,
  Alert,
  Slide,
  InputAdornment,
  alpha,
  Chip
} from "@mui/material";
import { motion } from "framer-motion";
import EmailIcon from '@mui/icons-material/Email';
import DiamondIcon from '@mui/icons-material/Diamond';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StarsIcon from '@mui/icons-material/Stars';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setTimeout(() => setSubscribed(false), 5000);
      setEmail('');
    }
  };

  const benefits = [
    { icon: <StarsIcon />, text: 'Early Access' },
    { icon: <TrendingUpIcon />, text: 'Market Insights' },
    { icon: <NotificationsActiveIcon />, text: 'Private Auctions' },
  ];

  return (
    <Box sx={{ 
      py: { xs: 12, md: 20 },
      background: 'linear-gradient(135deg, #F8F9FA 0%, #FFFFFF 100%)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Animated background elements */}
      <Box
        component={motion.div}
        animate={{
          scale: [1, 1.1, 1],
          rotate: [0, 180, 360],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        sx={{
          position: 'absolute',
          top: -150,
          right: -150,
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha('#C6A75E', 0.05)} 0%, transparent 70%)`,
          zIndex: 0,
        }}
      />
      <Box
        component={motion.div}
        animate={{
          scale: [1, 1.2, 1],
          rotate: [360, 180, 0],
        }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        sx={{
          position: 'absolute',
          bottom: -150,
          left: -150,
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha('#C6A75E', 0.05)} 0%, transparent 70%)`,
          zIndex: 0,
        }}
      />

      {/* Floating diamonds */}
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          initial={{ 
            x: Math.random() * window.innerWidth, 
            y: Math.random() * window.innerHeight,
            rotate: 0,
          }}
          animate={{
            y: [null, -30, 30, -30],
            x: [null, 30, -30, 30],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: Math.random() * 15 + 10,
            repeat: Infinity,
            ease: "linear",
          }}
          style={{
            position: 'absolute',
            opacity: 0.03,
            pointerEvents: 'none',
          }}
        >
          <DiamondIcon sx={{ fontSize: 40, color: '#C6A75E' }} />
        </motion.div>
      ))}

      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 8 },
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FEF9F0 100%)',
              border: '1px solid',
              borderColor: alpha('#C6A75E', 0.2),
              boxShadow: '0 20px 40px rgba(0,0,0,0.05)',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Animated diamond icon */}
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                rotate: [0, 180, 360],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              style={{
                position: 'absolute',
                top: -30,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 70,
                height: 70,
                backgroundColor: '#C6A75E',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 10px 30px rgba(198,167,94,0.3)',
                zIndex: 1,
              }}
            >
              <DiamondIcon sx={{ color: '#FFFFFF', fontSize: 35 }} />
            </motion.div>

            <Box sx={{ mt: 6 }}>
              <Chip
                label="EXCLUSIVE ACCESS"
                sx={{
                  backgroundColor: alpha('#C6A75E', 0.1),
                  color: '#C6A75E',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  fontSize: '0.7rem',
                  mb: 3,
                }}
              />

              <Typography variant="h3" gutterBottom sx={{ 
                fontWeight: 700, 
                mb: 2, 
                color: '#2D2D2D',
                fontSize: { xs: '1.8rem', md: '2.5rem' },
              }}>
                Join Our Private
                <Box component="span" sx={{ color: '#C6A75E', display: 'block', mt: 1 }}>
                  Collector List
                </Box>
              </Typography>

              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 5, 
                  color: '#616161',
                  maxWidth: '550px',
                  mx: 'auto',
                  fontSize: '1.1rem',
                  lineHeight: 1.6,
                }}
              >
                Be the first to access rare releases, private auctions, and exclusive 
                investment opportunities before they're available to the public.
              </Typography>

              {subscribed && (
                <Slide direction="down" in={subscribed}>
                  <Alert 
                    icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                    severity="success"
                    sx={{ 
                      mb: 3,
                      backgroundColor: alpha('#C6A75E', 0.1),
                      color: '#C6A75E',
                      border: `1px solid ${alpha('#C6A75E', 0.3)}`,
                      '& .MuiAlert-icon': {
                        color: '#C6A75E',
                      },
                    }}
                  >
                    Thank you for subscribing! Please check your email to confirm your subscription.
                  </Alert>
                </Slide>
              )}

              <Box 
                component="form" 
                onSubmit={handleSubmit}
                sx={{ maxWidth: '550px', mx: 'auto' }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    fullWidth
                    placeholder="Enter your email address"
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#FFFFFF',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 0 0 2px ${alpha('#C6A75E', 0.2)}`,
                        },
                        '&.Mui-focused': {
                          boxShadow: `0 0 0 2px ${alpha('#C6A75E', 0.3)}`,
                        },
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: '#C6A75E' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button 
                      type="submit"
                      variant="contained" 
                      size="large"
                      sx={{ 
                        px: 5, 
                        py: 1.8,
                        whiteSpace: 'nowrap',
                        minWidth: '160px',
                        background: 'linear-gradient(135deg, #C6A75E 0%, #B89A4F 100%)',
                        color: '#000000',
                        fontWeight: 700,
                        fontSize: '1rem',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #D4B86A 0%, #C6A75E 100%)',
                        },
                      }}
                    >
                      Subscribe Now
                    </Button>
                  </motion.div>
                </Stack>
              </Box>

              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block',
                  mt: 3,
                  color: '#9E9E9E',
                }}
              >
                By subscribing, you agree to our Privacy Policy and consent to receive updates.
                You can unsubscribe at any time.
              </Typography>

              {/* Benefits List with animation */}
              <Stack 
                direction="row" 
                spacing={4} 
                justifyContent="center" 
                sx={{ 
                  mt: 6,
                  flexWrap: 'wrap',
                  gap: 3,
                }}
              >
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit.text}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1.5,
                        color: '#616161',
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        backgroundColor: alpha('#C6A75E', 0.05),
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          backgroundColor: alpha('#C6A75E', 0.1),
                          transform: 'translateY(-3px)',
                        },
                      }}
                    >
                      <Box sx={{ color: '#C6A75E' }}>
                        {benefit.icon}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {benefit.text}
                      </Typography>
                    </Box>
                  </motion.div>
                ))}
              </Stack>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Newsletter;